import React, { useState, useEffect } from 'react';
import { GoogleMap, useJsApiLoader, Marker, MarkerClusterer, Autocomplete as GooglePlacesAutocomplete } from '@react-google-maps/api';
import { DeleteCampaignSites, FetchCampaignSites, FetchCampaignSitesInViewport, SaveCampaignSites } from '../../../../Api/MapApi';

import mapMarkerYellow from "../../../../Assets/map-marker-yellow.png";
import mapMarkerBlue from "../../../../Assets/map-marker-blue 1.png";
import { InputPrefix, MaskedTextBox, Switch } from '@progress/kendo-react-inputs';
import { SvgIcon } from '@progress/kendo-react-common';
import { searchIcon } from '@progress/kendo-svg-icons';
import mapStyle from "../../../../Assets/mapStyle"
import "./AdsMap.css"
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Loader } from '@progress/kendo-react-indicators';

const mapOptions = {
    styles: mapStyle,
    // center: { lat: 51.048615, lng: -114.070847 },
    maxZoom: 20,				// Magic number, more zoom is disorienting
    draggableCursor: "default",	// Show normal cursor on hover (on drag, changes to closed hand)
    disableDefaultUI: true,
    clickableIcons: false,
}

// Style all MarkerClusters to be black on yellow
const clusterOptions = {
    styles: [
        {
            textColor: "black",
            textSize: "13",
            height: 56,
            width: 56,
            // Icon image file needs to be hosted somewhere
            url: "https://unpkg.com/@googlemaps/markerclustererplus@1.0.3/images/m2.png"
        },
    ]
}

export default function AdsMap({ campaignId, ...props }) {
    //checks to see if the map api key works
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.MAP_API_KEY
    })

    const [isLoading, setIsLoading] = useState(false)
    const [pinList, setMapPinList] = useState(null)

    const [autocomplete, setAutocomplete] = useState(null)	// Google Places Autocomplete API instance
    const [searchTerm, setSearchTerm] = useState("");		// Current value of search field
    const [selectedPin, setSelectedPin] = useState(null);
    const [siteFilter, setSiteFilter] = useState("All");
    const [center, setCenter] = useState({
        // lat: 51.048615,
        // lng: -114.070847
    });

    const mapRef = React.useRef(null); // Store the map instance

    useEffect(() => {
        // Get user location
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {

                const { latitude, longitude } = position.coords;
                setCenter({ lat: latitude, lng: longitude }); // Set user's location as center
            }, () => {
                // Handle error case if location access is denied or fails
                // console.error("Geolocation is not available.");
                setCenter({ lat: 51.048615, lng: -114.070847 }); // Set user's location as center
            });
        } else {
            console.error("Geolocation is not supported by this browser.");
        }
    }, []);


    const FetchAdsMapData = async (bounds) => {
        try {
            if (!bounds || Object.values(bounds).some(value => value === undefined)) return;

            // Replace null values in bounds with 0
            const sanitizedBounds = Object.fromEntries(
                Object.entries(bounds).map(([key, value]) => [key, value ?? 0])
            );

            const selectionConverter = () => {
                if (siteFilter === "All") return null;
                if (siteFilter === "Selected") return true;
                return false;
            };

            const isSelectedObj = { isSelected: selectionConverter() };
            const boundsAltered = { ...sanitizedBounds, ...isSelectedObj };

            const mapPinsArray = await FetchCampaignSitesInViewport(boundsAltered);

            setMapPinList(mapPinsArray);
        } catch (error) {
            console.error("Error fetching map data:", error);
        } finally {
            setIsLoading(false);
        }
    };


    let handleMapViewportChange = React.useCallback(() => {
        if (mapRef.current !== null) {
            // Extract the latitudes and longitudes from the bounds
            const mapBounds = {
                CampaignId: campaignId,

                LatitudeNorth: mapRef.current.getBounds()?.getNorthEast().lat(),
                LatitudeSouth: mapRef.current.getBounds()?.getSouthWest().lat(),
                LongitudeEast: mapRef.current.getBounds()?.getNorthEast().lng(),
                LongitudeWest: mapRef.current.getBounds()?.getSouthWest().lng(),
            };

            // setBounds(mapBounds)
            FetchAdsMapData(mapBounds)
        }
    }, [mapRef])

    useEffect(() => {
        setIsLoading(true);

        FetchAdsMapData();
    }, []);

    // //Trigger when the filter gets changed
    useEffect(() => {
        if (mapRef.current !== null) {
            setIsLoading(true);
            const mapBounds = {
                CampaignId: campaignId,
                LatitudeNorth: mapRef.current.getBounds().getNorthEast().lat(),
                LatitudeSouth: mapRef.current.getBounds().getSouthWest().lat(),
                LongitudeEast: mapRef.current.getBounds().getNorthEast().lng(),
                LongitudeWest: mapRef.current.getBounds().getSouthWest().lng(),
            };
            FetchAdsMapData(mapBounds);
        }
    }, [siteFilter]); // Re-fetch data when siteFilter changes


    // // Handles Google Places Autocomplete option click, "enter" keypress, and "search" button click
    function handleSearch() {
        if (autocomplete !== null) {
            // Autocomplete option selected; move map to address of option
            if (autocomplete.getPlace().formatted_address) {
                // Projects will update on map change
                setSearchTerm(autocomplete.getPlace().formatted_address);	// Set search field value to address
                mapRef.current.fitBounds(autocomplete.getPlace().geometry.viewport);

                handleMapViewportChange()
            }
        }
        else
            alert("Error: Autocomplete not loaded yet!");
    }

    const handleMarkerClick = async (pin) => {
        if (selectedPin?.siteId === pin.siteId) {
            handlePinSelection(pin); // Toggle selection if clicking the same marker
        } else {
            setSelectedPin(pin);
            mapRef.current.panTo({ lat: pin.latitude, lng: pin.longitude });
        }
    };


    let handlePinSelection = async (pin) => {

        // Create a copy of the pinList to update the specific pin
        const updatedPinList = pinList.map((p) => {
            if (p.siteId === pin.siteId) {
                return { ...p, isSelected: !pin.isSelected }; // Toggle the selection state
            }
            return p;
        });

        // Determine the new selection state for the current pin
        const newPin = updatedPinList.find((p) => p.siteId === pin.siteId);

        // Set the updated pinList state
        setMapPinList(updatedPinList);

        // Update selectedPin state to reflect the change
        setSelectedPin((prevPin) => ({
            ...prevPin,
            isSelected: newPin.isSelected // Update isSelected based on the new state
        }));

        if (pin.isSelected) {
            let saveData = {
                DeleteList: [{
                    "CampaignId": campaignId,
                    "SiteId": pin.siteId,
                }]
            }

            try {
                await DeleteCampaignSites(saveData)
            } catch (error) {
                props.setAlertMessage(error)
                props.onTriggerAlert('error')
                console.log(error);
            }
        } else {
            let saveData = {
                SaveList: [{
                    "CampaignId": campaignId,
                    "SiteId": pin.siteId,
                }]
            }

            try {
                await SaveCampaignSites(saveData)
            } catch (error) {
                props.setAlertMessage(error)
                props.onTriggerAlert('error')
                console.log(error);
            }
        }
    }

    if (isLoading && !(center && Object.keys(center).length > 0)) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "80vh", // Makes it take full screen height
                    width: "100%", // Ensures it spans the full width
                }}
            >
                <Loader className='infinite-spinner' size='large' type='infinite-spinner' />
            </div>
        );
    }

    return isLoaded && !isLoading && (center && Object.keys(center).length > 0) ? (
        <div style={{ width: '100%', borderRadius: "8px" }}>
            {/* Legend Section */}
            <div className="map-legend">
                {/* <div className="legend-item">
                    <img src={"https://unpkg.com/@googlemaps/markerclustererplus@1.0.3/images/m2.png"} alt="Selected Marker" style={{ width: '24px', height: '26px' }} />
                    <span>Multiple Sites</span>
                </div> */}
                <div className="legend-item">
                    <img src={mapMarkerYellow} alt="Selected Marker" style={{ width: '20px', height: '26px' }} />
                    <span>Selected Site</span>
                </div>
                <div className="legend-item">
                    <img src={mapMarkerBlue} alt="Unselected Marker" style={{ width: '20px', height: '26px' }} />
                    <span>Unselected Site</span>
                </div>
            </div>

            {/* {console.log(center && Object.keys(center).length > 0 ? center : {
                lat: 51.048615,
                lng: -114.070847
            })} */}

            <GoogleMap
                options={mapOptions}
                mapContainerStyle={{
                    height: '80vh',
                    borderRadius: "8px",
                    width: "100%"
                }}
                center={center}
                zoom={12}
                // onUnmount={onUnmount}
                onIdle={() => handleMapViewportChange()}
                onLoad={(mapInstance) => {
                    mapRef.current = mapInstance;
                    handleMapViewportChange(); // Call it once when map loads
                }}
            >
                <GooglePlacesAutocomplete
                    onLoad={newInstance => {
                        // Sets instance of Autocomplete to be referenced
                        setAutocomplete(newInstance);
                    }}
                    onPlaceChanged={handleSearch}>
                    <MaskedTextBox
                        style={{
                            width: "40%", // Makes the input responsive
                            maxWidth: "400px", // Prevents it from getting too wide
                            minWidth: "15%", // Ensures it doesn't shrink too much
                            margin: "12px 22px",
                            border: "2px solidrgb(120, 120, 120)", // A bold border color
                            borderRadius: "4px", // Rounded corners
                            boxShadow: " 0px 0px 8px 2px lightgray",
                        }}
                        placeholder="Search site address"
                        fullWidth
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}

                        prefix={() => (
                            <InputPrefix orientation="horizontal">
                                <SvgIcon icon={searchIcon} />
                            </InputPrefix>
                        )}
                    />
                </GooglePlacesAutocomplete>

                {/* Show only selected places filter */}
                <DropDownList
                    data={["All", "Selected", "Not Selected"]}
                    value={siteFilter}
                    onChange={(e) => setSiteFilter(e.value)}
                    style={{
                        width: "12%",
                        margin: "0px 22px",
                        border: "2px solidrgb(120, 120, 120)", // A bold border color
                        borderRadius: "4px", // Rounded corners
                        boxShadow: " 0px 0px 8px 2px lightgray",
                        backgroundColor: "white"
                    }}
                />

                {selectedPin && (
                    <div className={`selected-pin-container ${selectedPin ? "show" : ""}`}>
                        <div className="selected-pin-header">
                            <div style={{ display: "flex", float: "right" }}>
                                <span
                                    onClick={() => setSelectedPin(null)}
                                    className="selected-pin-close k-icon k-font-icon k-icon-lg k-i-close">
                                </span>
                            </div>
                        </div>
                        <p><strong>Name:</strong> {selectedPin.name}</p>
                        <p><strong>Address:</strong> {selectedPin.address}</p>
                        <p><strong>Followers:</strong> {selectedPin.siteFollowerCount || 0}</p>
                        <p><strong style={{ marginRight: "8px" }}>Selected:</strong>
                            <Switch
                                onChange={() => { handlePinSelection(selectedPin) }}
                                checked={selectedPin.isSelected}
                                onLabel={"Yes"}
                                offLabel={"No"}
                            /></p>
                    </div>
                )}

                {/* MarkerClusterer to handle clustering */}
                {/* <MarkerClusterer
                    options={clusterOptions}>
                    {(clusterer) => */}
                {pinList?.map((pin, index) => (
                    <Marker
                        key={index}
                        position={{
                            lat: pin.latitude,
                            lng: pin.longitude
                        }}
                        // clusterer={clusterer} 
                        // Optionally add a custom icon
                        icon={{
                            url: (pin.isSelected)
                                ? mapMarkerYellow
                                : mapMarkerBlue,
                            scaledSize: new window.google.maps.Size(30, 40)
                        }}
                        onClick={() => handleMarkerClick(pin)}
                    />
                ))
                }
                {/* </MarkerClusterer> */}
            </GoogleMap >
        </div >
    ) : (
        <></>
    )
}
